import React, {
    useCallback, useState, useEffect, useRef, useContext
} from 'react';
import _ from 'lodash';
import { withRouter } from 'react-router-dom';
import { useDependencies } from 'gw-portals-dependency-react';
import { withAuthenticationContext } from 'gw-digital-auth-react';
import { readViewModelValue } from 'gw-jutro-adapters-react';
import { useModal } from '@jutro/components';
import { AdacConfirmationModal, AdacErrorModal, AdacButtonWithLoader, CommunicationVariablesContext } from 'gw-capability-fnol-adac-react';
import { useTranslator } from '@jutro/locale';
import {
    ViewModelServiceContext,
    withViewModelService,
    ViewModelForm
} from 'gw-portals-viewmodel-react';
import SignatureCanvas from 'react-signature-canvas';
import cx from 'classnames';
import moment from 'moment';
import { useValidation } from 'gw-portals-validation-react';
import { ErrorCodes } from 'gw-portals-util-adac-js';
import styles from './ConfidentialityWaiver.module.scss';
import metadata from './ConfidentialityWaiver.metadata.json5';
import cwMessages from './ConfidentialityWaiver.messages';
import CWSection from './CWSection';
import CWCheckboxField from './CWCheckboxField';

function HtmlContent(props) {
    const elem = useRef(null);
    useEffect(() => {
        elem.current.innerHTML = props.content;
    }, []);
    return (
        <p
            ref={elem}
            className={props.className || ''}></p>
    );
}

function ConfidentialityWaiver(props) {
    const { showModal } = useModal();
    const {
        history,
        location
    } = props;
    const translator = useTranslator();
    const [isCWChecked, setIsCWChecked] = useState();
    const [claimDetailsData, setClaimDetailsData] = useState(
        { claim: { documents: [] }, activities: [] }
    );
    const { ClaimService } = useDependencies('ClaimService');
    const viewModelService = useContext(ViewModelServiceContext);
    const [isLoading, setLoadingState] = useState(true);
    const canvasRef = useRef({});
    const [isSigningDocument, setIsSigningDocument] = useState(false);
    const [isDocumentSigned, setIsDocumentSigned] = useState(false);
    const [isSendingDocument, setIsSendingDocument] = useState(false);
    const [showErrors, setShowErrors] = useState(false);
    const [ebsVM, setEbsVM] = useState(null);
    const { communicationVariables } = useContext(CommunicationVariablesContext) 
    const { token, sessionUUID } = communicationVariables;
    const singingPlaceRef = useRef(null);
    const legalRepresentativeRef = useRef(null);

    const {
        onValidate
    } = useValidation('ConfidentialityWaiver');

    useEffect(() => {
        const signingPlaceElement = document.getElementById('signingPlace');
        const legalRepresentativeElement = document.getElementById('legalRepresentative');
    
        if (signingPlaceElement) {
            signingPlaceElement.autocomplete = 'new-password';
        }
    
        if (legalRepresentativeElement) {
            legalRepresentativeElement.autocomplete = 'new-password';
        }
    }, []);

    useEffect(() => {
        const now = moment();
        const vm = viewModelService.create(
            {
                sessionUUID,
                signingDate: {
                    year: now.year(),
                    month: now.month(),
                    day: now.date()
                },
                checkboxes: {
                    checkbox1: false,
                    checkbox2: false,
                    checkbox3: false,
                    checkbox4: false,
                    checkbox5: false,
                },
                choose: true
            },
            'cc',
            'adac.edge.capabilities.claim.ebs.dto.EbsDTO'
        );
        setEbsVM(vm);
    }, []);

    const updateEbsVM = useCallback((value, path) => {
        const newVM = viewModelService.clone(ebsVM);
        _.set(newVM, `${path}.value`, value);
        setEbsVM(newVM);
    }, [ebsVM, viewModelService, setEbsVM]);

    const redirect = useCallback(() => {
        const redirectPath = '/documents';
        history.push({
            pathname: redirectPath
        });
    }, [history, token, sessionUUID]);

    const handleError = useCallback((message, bool) => {
        showModal(
            <AdacErrorModal message={translator(message)} token={token} redirectEnabled={bool} />
        );
    }, [showModal, translator, token]);

    const getClaimDetailsResponse = useCallback(async () => {
        if (!token || !sessionUUID) { return; }
        Promise.all([ClaimService.getPatientData(token, sessionUUID),
            ClaimService.hasEbsSigned(token, sessionUUID)])
            .then((response) => {
                const [patientDetails, hasEbsSigned] = response;
                if (hasEbsSigned) {
                    redirect();
                }
                const responseData = {
                    patient: patientDetails,
                    isEbsSigned: hasEbsSigned
                };
                setClaimDetailsData(responseData);
                setIsCWChecked(responseData.isEbsSigned);
            })
            .catch((error) => {
                if (error.appErrorCode === ErrorCodes.SESSION_EXPIRED) {
                    history.push({
                        pathname: '/sessionexpired'
                    });
                } else {
                    handleError(cwMessages.failedToGetPatientData, token, true);
                }
            })
            .finally(() => {
                setLoadingState(false);
            });
    }, [token, sessionUUID, history, ClaimService, redirect, handleError]);

    const cwValueChangeFn = useCallback((value) => {
        setIsCWChecked(value);
    }, []);

    const validate = useCallback(() => {
        setShowErrors(false);
        if (!ebsVM.aspects.subtreeValid) {
            setShowErrors(true);
            window.scrollTo(0, 0);
            return false;
        }
        return true;
    }, [ebsVM]);

    const signDocument = useCallback(() => {
        if (!validate()) {
            return;
        }
        setIsSigningDocument(!isSigningDocument);
    }, [isSigningDocument, validate]);

    const onSignature = useCallback(() => {
        setIsDocumentSigned(!canvasRef.current.isEmpty());
    }, []);

    const correctTheSignature = useCallback(() => {
        canvasRef.current.clear();
        setIsDocumentSigned(false);
    }, []);

    const showModalAndRedirect = useCallback(async (message) => {
        await showModal(
            <AdacConfirmationModal
                message={translator(message)}
                confirmButtonText={translator(cwMessages.close)}
            />
        );
        redirect();
    }, [showModal, redirect, translator]);

    const cWSendClick = useCallback(async () => {
        if (!validate()) {
            return;
        }
        const signatureData = canvasRef.current.toDataURL('image/png');
        setIsSendingDocument(true);
        try {
            await ClaimService.signAndStoreEbs(
                token,
                {
                    signatureData,
                    ...ebsVM.value
                }
            );
            showModalAndRedirect(cwMessages.submittedSuccessfully);
        } catch (error) {
            if (error.appErrorCode === ErrorCodes.SESSION_EXPIRED) {
                history.push({
                    pathname: '/sessionexpired'
                });
            } else {
                handleError(cwMessages.ebsFailed, false);
                correctTheSignature();
            }
        } finally {
            setIsSendingDocument(false);
        }
    }, [
        history,
        token,
        ClaimService,
        showModalAndRedirect,
        handleError,
        ebsVM,
        correctTheSignature,
        validate
    ]);

    const getPrimaryContactName = useCallback(() => {
        if (_.has(claimDetailsData, 'patient')) {
            return claimDetailsData.patient.displayName;
        }
        return '';
    }, [claimDetailsData]);

    const onToggleValueChange = useCallback((value) => {
        const newVM = viewModelService.clone(ebsVM);
        _.set(newVM, 'checkboxes.checkbox2.value', false);
        _.set(newVM, 'choose.value', value);
        setEbsVM(newVM);
    }, [ebsVM, viewModelService]);

    useEffect(() => {
        if (isLoading) {
            getClaimDetailsResponse();
        }
    }, [isLoading, getClaimDetailsResponse]);


    // eslint-disable-next-line react-hooks/exhaustive-deps
    const overrides = {
        cwPageLoader: {
            loaded: !isLoading,
            loadingMessage: '...'
        },
        contentContainer: {
            visible: !isLoading
        },
        cwCcheckboxField: {
            value: isCWChecked,
            readOnly: claimDetailsData.isEbsSigned
        },
        cwButtonSend: {
            visible: claimDetailsData.isEbsSigned,
            disabled: !isCWChecked
        },
        signtTheDocumentButton: {
            visible: !isSigningDocument
        },
        cwSignPadSection: {
            visible: isSigningDocument
        },
        signaturecanvas: {
            canvasProps: { className: cx(styles.sigCanvasContent) },
            ref: canvasRef
        },
        correctSignatureButton: {
            disabled: !isDocumentSigned || isSendingDocument
        },
        sendButton: {
            onClick: cWSendClick,
            disabled: !isDocumentSigned || isSendingDocument,
            label: translator(cwMessages.sendButton),
            isLoading: isSendingDocument
        },
        toggleInfo: {
            visible: _.get(ebsVM, 'value.choose') === false
        },
        toggleError: {
            visible: showErrors
        },
        firstchoicecontainer: {
            visible: _.get(ebsVM, 'value.choose') === false
        },
        secoundchoicecontainer: {
            visible: _.get(ebsVM, 'value.choose') === true
        },
        signatory: {
            value: _.get(ebsVM, 'value.legalRepresentative') || (`${_.get(claimDetailsData, 'patient.firstName')} ${_.get(claimDetailsData, 'patient.lastName')}`)
        },
        firstname: {
            value: _.get(claimDetailsData, 'patient.firstName')
        },
        lastname: {
            value: _.get(claimDetailsData, 'patient.lastName')
        },
        dateOfBirth: {
            value: _.get(claimDetailsData, 'patient.dateOfBirth_Adac')
        },
        signingPlace: {
            showErrors,
            ref: singingPlaceRef
        },
        legalRepresentative: {
            ref: legalRepresentativeRef
        },
        toggle1: {
            onValueChange: onToggleValueChange,
            className: 'toggleEbs'
        }
    };

    Object.keys(_.get(ebsVM, 'value.checkboxes') || {}).forEach((checkbox) => {
        overrides[checkbox] = { showErrors };
    });

    const readValue = useCallback(
        (id, path) => {
            return readViewModelValue(metadata.pageContent, ebsVM, id, path, overrides);
        },
        [ebsVM, overrides]
    );

    const resolvers = {
        resolveValue: readValue,
        resolveClassNameMap: styles,
        resolveComponentMap: {
            signaturecanvas: SignatureCanvas,
            cwsection: CWSection,
            cwcheckboxfield: CWCheckboxField,
            htmlcontent: HtmlContent,
            buttonWitLoader: AdacButtonWithLoader
        },
        resolveCallbackMap: {
            signDocument: signDocument,
            cwValueChangeFn: cwValueChangeFn,
            redirectClick: redirect,
            onCWSendClick: cWSendClick,
            getPrimaryContactName: getPrimaryContactName,
            correctTheSignatureClick: correctTheSignature,
            onSignature: onSignature,
            updateEbsVM
        }
    };

    return (
        <ViewModelForm
            model={ebsVM}
            uiProps={metadata.pageContent}
            overrideProps={overrides}
            resolveValue={readValue}
            onValueChange={updateEbsVM}
            callbackMap={resolvers.resolveCallbackMap}
            classNameMap={resolvers.resolveClassNameMap}
            componentMap={resolvers.resolveComponentMap}
            onValidationChange={onValidate}
        />
    );
}

export default withViewModelService(withRouter(withAuthenticationContext(ConfidentialityWaiver)));
